import * as Sentry from "@sentry/react-native"
import Constants from "expo-constants"
import { Platform } from "react-native"

type SentryExtraConfig = {
  sentryDsn?: string
  sentryEnvironment?: string
}

const readExtraConfig = () =>
  (Constants.expoConfig?.extra ?? {}) as SentryExtraConfig & Record<string, unknown>

const readOptionalValue = (value: string | undefined) =>
  typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined

const readOptionalEnv = (name: "NODE_ENV") => {
  const value = process.env[name]
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined
}

const readSentryDsn = () => {
  const extra = readExtraConfig()
  return readOptionalValue(process.env.EXPO_PUBLIC_SENTRY_DSN) ?? extra.sentryDsn
}

const readSentryEnvironment = () => {
  const extra = readExtraConfig()
  return (
    readOptionalValue(process.env.EXPO_PUBLIC_SENTRY_ENVIRONMENT) ??
    extra.sentryEnvironment ??
    readOptionalEnv("NODE_ENV") ??
    "development"
  )
}

const readBooleanValue = (value: string | undefined, fallback: boolean) => {
  const normalizedValue = readOptionalValue(value)
  if (!normalizedValue) return fallback

  return !["0", "false", "off", "no"].includes(normalizedValue.toLowerCase())
}

const readSentryEnabled = () => readBooleanValue(process.env.EXPO_PUBLIC_SENTRY_ENABLED, true)

let initialized = false

export const initializeSentry = () => {
  if (initialized) return
  initialized = true

  const dsn = readSentryDsn()
  const enabled = Boolean(dsn) && readSentryEnabled()

  Sentry.init({
    dsn,
    enabled,
    environment: readSentryEnvironment(),
    debug: readBooleanValue(process.env.EXPO_PUBLIC_SENTRY_DEBUG, false),
    sendDefaultPii: false,
    enableAutoSessionTracking: enabled && Platform.OS !== "web",
  })
}

export { Sentry }
